// src/components/pages/admin-dash/AdminMailingList.jsx
import React, { useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Field from "../../ui/Field";
import Badge from "../../ui/Badge";
import AdminTable from "./AdminTable";

/**
 * Admin view over the mailing list (MailingListAdminResource,
 * /admin/mailing-list) — subscribe/unsubscribe are public-facing, this
 * is the admin-only "who's actually on it" view + CSV export
 * (1-ADMIN_ROADMAP.md item 2.5).
 */
const STATUS_OPTIONS = [
  { value: "",      label: "All"      },
  { value: "true",  label: "Active"   },
  { value: "false", label: "Unsubscribed" },
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

export default function AdminMailingList() {
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [didSearch, setDidSearch] = useState(false);

  const fetchSubscribers = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (status !== "") params.active = status;
      if (search.trim()) params.search = search.trim();
      const res = await axiosInstance.get("/admin/mailing-list/list-by-filter", { params });
      setSubscribers(Array.isArray(res.data) ? res.data : []);
      setDidSearch(true);
    } catch {
      setError("Failed to load subscribers.");
      setSubscribers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStatus(""); setSearch("");
    setSubscribers([]); setDidSearch(false); setError("");
  };

  const exportCsv = () => {
    if (subscribers.length === 0) return;
    const headers = ["id", "email", "name", "active", "subscribedDate", "unsubscribedDate"];
    const lines = [headers.join(",")];
    subscribers.forEach((s) => {
      const row = [
        s.id, s.email, s.name, s.active,
        s.subscribedDate?.split?.("T")[0] || "",
        s.unsubscribedDate?.split?.("T")[0] || "",
      ].map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`);
      lines.push(row.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mailing-list-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = [
    { key: "id", label: "ID", sort: true, width: "70px",
      render: (s) => <span className="tabular-nums" style={{ color: "var(--txt-2)" }}>#{s.id}</span> },
    { key: "email", label: "Email", sort: true, value: (s) => s.email || "",
      render: (s) => <span style={{ color: "var(--txt)" }}>{s.email}</span> },
    { key: "name", label: "Name", sort: true, value: (s) => s.name || "",
      render: (s) => s.name || <span style={{ color: "var(--txt-3)" }}>—</span> },
    { key: "active", label: "Status", sort: true, align: "center", width: "110px",
      value: (s) => s.active ? 1 : 0,
      render: (s) => s.active ? <Badge variant="teal">Active</Badge> : <Badge variant="neutral">Unsubscribed</Badge> },
    { key: "subscribedDate", label: "Subscribed", sort: true, value: (s) => s.subscribedDate || "",
      render: (s) => <span className="text-[12px]" style={{ color: "var(--txt-2)" }}>{fmtDate(s.subscribedDate)}</span> },
    { key: "unsubscribedDate", label: "Unsubscribed", sort: true, value: (s) => s.unsubscribedDate || "",
      render: (s) => <span className="text-[12px]" style={{ color: "var(--txt-2)" }}>{fmtDate(s.unsubscribedDate)}</span> },
  ];

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
          Mailing list
        </h1>
        <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
          Search subscribers by status or email/name, export for a campaign.
        </p>
      </header>

      <Card variant="soft" className="!p-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
          <SelectField label="Status" value={status} onChange={setStatus} options={STATUS_OPTIONS} />
          <Field label="Search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Email or name" className="!mb-0" />
          <div className="flex items-end gap-2">
            <Button icon="search" onClick={fetchSubscribers} loading={loading} size="md">Search</Button>
            {(status || search || didSearch) && (
              <button
                type="button"
                onClick={handleReset}
                className="text-[12px] hover:underline px-1 pb-3"
                style={{ color: "var(--txt-2)" }}
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </Card>

      {error && (
        <div
          className="rounded-xl px-4 py-3 text-[13px]"
          style={{ background: "var(--red-dim)", border: "1px solid var(--red-border)", color: "var(--red)" }}
        >
          {error}
        </div>
      )}

      <AdminTable
        title={didSearch ? `${subscribers.length} subscribers` : undefined}
        rightHeaderSlot={
          subscribers.length > 0 && (
            <Button icon="download" size="sm" variant="ghost" onClick={exportCsv}>Export CSV</Button>
          )
        }
        columns={columns}
        rows={subscribers}
        rowKey="id"
        loading={loading}
        emptyIcon="mail"
        emptyMessage={didSearch ? "No subscribers match these filters." : "Search to see subscribers."}
        defaultSort={{ key: "subscribedDate", dir: "desc" }}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-[12px] font-medium mb-1.5 tracking-wide" style={{ color: "var(--txt)" }}>
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3.5 py-[11px] text-[14px] outline-none rounded-full border appearance-none cursor-pointer pr-9"
          style={{ background: "var(--surf-2)", color: "var(--txt)", borderColor: "var(--border-2)" }}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[10px]" style={{ color: "var(--txt-3)" }}>▾</span>
      </div>
    </div>
  );
}
